import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import JsonEditor from '../../JsonEditor';
import config from '../../../config';
import {
  getActiveSiteId,
  getSite,
  getThumbnails,
  requestModelThumbnail,
  updateSite,
} from '../../../bundles/sites';
import {
  getSiteSchema,
  siteUISchema,
} from '../../SiteCreateView/components/SiteCreateModal/schemas/siteSchema';
import { mailRegex, phoneRegex } from '../../../utils';
import { getAllModels, PENDING, PROCESSING } from '../../../bundles/models';

const hasPendingOrProcessingOutputs = (outputs) =>
  (outputs || []).some((o) => [PENDING, PROCESSING].includes(o.status));

const transformErrors = (errors) =>
  errors.map((error) => {
    switch (error.name) {
      case 'maximum':
      case 'minimum': {
        if (error.property === '.lat') error.message = 'Enter valid Latitude';
        else if (error.property === '.lng') error.message = 'Enter valid Longitude';
        break;
      }
      case 'required': {
        let property = error.property.includes('.') ? error.property.slice(1) : error.property;
        if (property.indexOf('location') === 0) {
          [, property] = property.split('.');
        }
        if (property === 'lat') error.message = 'Enter Latitude';
        else if (property === 'lng') error.message = 'Enter Longitude';
        else error.message = `Enter ${property.slice(0, 1).toUpperCase() + property.slice(1)}`;
        break;
      }

      default:
    }

    return error;
  });

const SiteView = memo(
  ({
    siteId, // when editing site directly from /sites
    onSaveCallback,
  }) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const activeSiteId = useSelector(getActiveSiteId);
    const theId = siteId || activeSiteId;
    const site = useSelector((state) => getSite(state, theId));

    const thumbnails = useSelector(getThumbnails);
    const hashmap = useSelector(getAllModels);
    const [siteModels, setSiteModels] = useState([]);
    const [selectedModel, setSelectedModel] = useState(null);
    const requestedThumbnailsReference = useRef(new Set());

    useEffect(() => {
      setSiteModels(() => Object.values(hashmap));
    }, [hashmap]);

    useEffect(() => {
      for (const model of siteModels) {
        for (const version of model.versions) {
          const key = `${model.id}/${version.id}`;
          if (
            !hasPendingOrProcessingOutputs(version.outputs) &&
            !thumbnails[key] &&
            !requestedThumbnailsReference.current.has(key)
          ) {
            requestedThumbnailsReference.current.add(key);
            dispatch(requestModelThumbnail(model.id, version.id));
          }
        }
      }
    }, [siteModels, thumbnails, dispatch]);

    const schema = useMemo(
      () => getSiteSchema(null, siteModels, false, config.onPremise),
      [siteModels]
    );
    const [siteData, setSiteData] = useState({});

    useEffect(() => {
      setSiteData({
        name: site?.name,
        customer: site?.customer,
        isTemplate: site?.isTemplate,
        model: site?.model || null,
        version: site?.version || null,
        siteImage: {
          linkSiteImage: site?.model,
        },
        location: {
          city: site?.location?.city,
          country: site?.location?.country,
          lat: site?.location?.lat,
          lng: site?.location?.lng,
        },
        support: {
          name: site?.support?.name,
          email: site?.support?.email,
          phone: site?.support?.phone,
        },
        template: site?.template,
      });
    }, [site]);

    const onFormChange = useCallback(
      ({ formData: siteProperties }) => {
        const linkedModelId = siteProperties?.siteImage?.linkSiteImage;
        const linkedModel = linkedModelId
          ? siteModels.find((model) => model.id === linkedModelId)
          : null;
        setSelectedModel(linkedModel || null);
        setSiteData((previousData) => ({
          ...previousData,
          ...siteProperties,
          model: linkedModelId,
          version: linkedModel?.versions[0]?.id || null,
        }));
      },
      [siteModels]
    );

    const onSubmit = useCallback(
      (siteData) => {
        dispatch(updateSite(theId, siteData, onSaveCallback));
      },
      [theId, onSaveCallback, dispatch]
    );

    const onClose = () => {
      history.push(`/sites/${activeSiteId}/admin/`);
    };

    const customValidate = useCallback((formData, errors) => {
      if (formData.support.email && !mailRegex.test(formData.support.email)) {
        errors.support.email.addError('Enter valid email');
      }
      if (formData.support.phone && !phoneRegex.test(formData.support.phone)) {
        errors.support.phone.addError(
          'Phone number must start with a plus sign (+) and country code, end in a digit, and should contain between 6-15 digits, optionally separated by single spaces or dashes.'
        );
      }
      return errors;
    }, []);

    return (
      <div className="edit-site-content">
        <JsonEditor
          title="Site"
          formData={siteData}
          schema={schema}
          onFormChange={onFormChange}
          uiSchema={siteUISchema}
          context={{ ...siteData, selectedModel, siteId: site?.id, org: site?.org }}
          initialEditMode={true}
          showEditButton={false}
          saveButtonText={'Save'}
          cancelCallback={onClose}
          onFormSubmit={({ formData }) => onSubmit(formData)}
          customValidate={customValidate}
          customTransformErrors={(errors) => transformErrors(errors)}
          editorOnly
        />
      </div>
    );
  }
);

export default SiteView;
